import type { Card, HistoryGame, HistoryResponse, RoundScoreBreakdown } from '../shared/types';
import { el, showScreen } from './api';
import { handleBid, handleNextRound, handlePlayCard, leaveGame } from './actions';
import {
  bidInFlight,
  getState,
  playInFlight,
  playerName,
  viewerSeat,
} from './store';

export const SEAT_NAMES = ['South', 'West', 'North', 'East'];
export const SUIT_SYMBOL: Record<string, string> = { spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' };
export const SUIT_COLOR: Record<string, string> = {
  spades: '#7c3aed',
  hearts: '#dc2626',
  diamonds: '#eab308',
  clubs: '#22c55e',
};
export const RANK_DISPLAY: Record<number, string> = {
  2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10',
  11: 'J', 12: 'Q', 13: 'K', 14: 'A',
};
export const PLAYER_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444'];

let lastTrickSig = '';
let lastHandSig = '';
let lastScoreSig = '';
let lastPlayerSig: Record<string, string> = {};
let lastOverlayKey = '';

let showFinishedTrick = false;

export function holdFinishedTrick(): void {
  showFinishedTrick = true;
}

export function releaseFinishedTrick(): void {
  showFinishedTrick = false;
  lastTrickSig = '';
}

export function isShowingFinishedTrick(): boolean {
  return showFinishedTrick;
}

export function resetRenderCache(): void {
  showFinishedTrick = false;
  lastTrickSig = '';
  lastHandSig = '';
  lastScoreSig = '';
  lastOverlayKey = '';
  lastPlayerSig = {};
}

export function isGameScreenActive(): boolean {
  return !el('game-screen').classList.contains('hidden');
}

function makeCardEl(card: Card, isPlayable: boolean, cardIdx: number): HTMLElement {
  const div = document.createElement('div');
  div.className = `card suit-${card.suit}${isPlayable ? ' playable' : ''}`;
  div.dataset.cardIndex = String(cardIdx);
  div.dataset.suit = card.suit;

  const top = document.createElement('div');
  top.className = 'card-corner card-top';
  top.innerHTML = `<span class="rank">${RANK_DISPLAY[card.rank]}</span><span class="suit">${SUIT_SYMBOL[card.suit]}</span>`;

  const center = document.createElement('div');
  center.className = 'card-center';
  center.textContent = SUIT_SYMBOL[card.suit];
  center.style.color = SUIT_COLOR[card.suit];

  const bot = document.createElement('div');
  bot.className = 'card-corner card-bot';
  bot.innerHTML = `<span class="rank">${RANK_DISPLAY[card.rank]}</span><span class="suit">${SUIT_SYMBOL[card.suit]}</span>`;

  div.appendChild(top);
  div.appendChild(center);
  div.appendChild(bot);
  return div;
}

function makeCardBack(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'card card-back';
  div.innerHTML = '<div class="card-back-inner">♠</div>';
  return div;
}

function makeTrickCardEl(card: Card | null, playerIndex: number): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = `trick-card-slot player-${playerIndex}-slot`;
  wrapper.id = `trick-slot-${playerIndex}`;
  if (card) {
    const cardEl = makeCardEl(card, false, -1);
    cardEl.classList.add('trick-card');
    wrapper.appendChild(cardEl);
  } else {
    const empty = document.createElement('div');
    empty.className = 'trick-empty';
    wrapper.appendChild(empty);
  }
  return wrapper;
}

export function renderTrickSlot(playerIndex: number, card: Card): void {
  const slot = document.getElementById(`trick-slot-${playerIndex}`);
  if (!slot) return;
  slot.innerHTML = '';
  const cardEl = makeCardEl(card, false, -1);
  cardEl.classList.add('trick-card');
  slot.appendChild(cardEl);
  lastTrickSig = '';
}

export function flashTrickWinner(winner: number | undefined): void {
  if (winner === undefined) return;
  const slot = document.getElementById(`trick-slot-${winner}`);
  if (!slot) return;
  slot.classList.add('trick-winner-flash');
  setTimeout(() => slot.classList.remove('trick-winner-flash'), 800);
}

function seatElId(playerIndex: number): string {
  if (playerIndex === viewerSeat) return 'human-info';
  const around: Record<number, string> = {
    [(viewerSeat + 1) % 4]: 'west-player',
    [(viewerSeat + 2) % 4]: 'north-player',
    [(viewerSeat + 3) % 4]: 'east-player',
  };
  return around[playerIndex] ?? 'human-info';
}

export function showBidToast(playerIndex: number, bid: number): void {
  const toast = document.createElement('div');
  toast.className = 'bid-toast';
  toast.style.background = PLAYER_COLORS[playerIndex];
  const gs = getState();
  const toastLabel = playerIndex === viewerSeat
    ? 'You'
    : (gs?.players[playerIndex]?.name ?? SEAT_NAMES[playerIndex]);
  toast.textContent = `${toastLabel}: ${bid === 0 ? 'NIL' : `Bid ${bid}`}`;
  // Measured off the seat, then parked on the body: renders rebuild the seat
  // boxes, which would take a toast living inside one with them.
  const seat = document.getElementById(seatElId(playerIndex))?.getBoundingClientRect();
  document.body.appendChild(toast);
  toast.style.top = seat ? `${seat.top + seat.height / 2}px` : '50%';
  toast.style.left = seat ? `${seat.left + seat.width / 2}px` : '50%';
  setTimeout(() => {
    toast.classList.add('bid-toast-hide');
    setTimeout(() => toast.remove(), 500);
  }, 1200);
}

export function renderGame(): void {
  const gameState = getState();
  if (!gameState) return;
  if (gameState.phase === 'waiting') renderWaitingRoom();
  else el('waiting-room').classList.add('hidden');
  renderScorePanel();
  renderPlayers();
  renderTrick();
  renderHumanHand();
  renderActionPanel();
  renderMessage();
}

function renderWaitingRoom(): void {
  const gameState = getState();
  if (!gameState) return;
  const room = el('waiting-room');
  room.classList.remove('hidden');
  el('join-code-value').textContent = gameState.joinCode;
  const seatsEl = el('waiting-seats');
  seatsEl.innerHTML = '';
  for (const player of gameState.players) {
    if (!player.isHuman) continue;
    const row = document.createElement('div');
    const joined = player.joined;
    row.className = `waiting-seat-row ${joined ? 'seat-joined' : 'seat-waiting'}`;
    const isMe = player.index === viewerSeat;
    const seatLabel = SEAT_NAMES[player.index] + (player.index === 0 ? ' (creator)' : '');
    const displayName = joined ? player.name + (isMe ? ' (you)' : '') : '???';
    const statusText = joined ? '✓ Joined' : 'Waiting…';
    row.innerHTML = `
      <div class="seat-dot"></div>
      <div class="waiting-seat-name">${seatLabel} — ${displayName}</div>
      <div class="waiting-seat-status">${statusText}</div>
    `;
    seatsEl.appendChild(row);
  }
}

function trickSignature(trick: (Card | null)[] | undefined): string {
  return (trick ?? []).map(c => (c ? `${c.suit}${c.rank}` : '-')).join('|');
}

function renderScorePanel(): void {
  const gameState = getState();
  if (!gameState) return;
  const panel = el('score-panel');
  const sig = `${gameState.roundNumber}:${gameState.teamScores.join(',')}:${gameState.teamBags.join(',')}:${gameState.dealer}`;
  if (sig === lastScoreSig && panel.innerHTML) return;
  lastScoreSig = sig;
  const viewerTeam = (viewerSeat === 0 || viewerSeat === 2) ? 0 : 1;
  const partnerLabel = SEAT_NAMES[(viewerSeat + 2) % 4];
  const opp1Label = SEAT_NAMES[(viewerSeat + 1) % 4];
  const opp2Label = SEAT_NAMES[(viewerSeat + 3) % 4];
  panel.innerHTML = `
    <div class="score-team team-us">
      <span class="team-label">🫂 US (You+${partnerLabel})</span>
      <span class="score-val">${gameState.teamScores[viewerTeam]}</span>
      <span class="bags-val">Bags: ${gameState.teamBags[viewerTeam]}</span>
    </div>
    <div class="score-divider">vs</div>
    <div class="score-team team-them">
      <span class="team-label">🤖 THEM (${opp1Label}+${opp2Label})</span>
      <span class="score-val">${gameState.teamScores[1 - viewerTeam]}</span>
      <span class="bags-val">Bags: ${gameState.teamBags[1 - viewerTeam]}</span>
    </div>
    <div class="round-label">Round ${gameState.roundNumber} · Goal: ${gameState.winScore}</div>
    <div class="round-label">Dealer: ${SEAT_NAMES[gameState.dealer]} · ${SEAT_NAMES[(gameState.dealer + 1) % 4]} bids first</div>
  `;
}

function handPointsChipHTML(playerIndex: number): string {
  const gameState = getState();
  if (!gameState) return '';
  const idle = gameState.phase === 'waiting' || gameState.phase === 'bidding';
  const team = (playerIndex === 0 || playerIndex === 2) ? 0 : 1;
  const seatA = team === 0 ? 0 : 1;
  const seatB = team === 0 ? 2 : 3;
  const pA = gameState.players[seatA];
  const pB = gameState.players[seatB];
  const tricks = pA.tricks + pB.tricks;
  const bidA = pA.bid ?? 0;
  const bidB = pB.bid ?? 0;
  let handPts = 0;
  if (pA.isNilBid) handPts += (pA.tricks === 0 ? 100 : -100);
  if (pB.isNilBid) handPts += (pB.tricks === 0 ? 100 : -100);
  const nonNilBid = (pA.isNilBid ? 0 : bidA) + (pB.isNilBid ? 0 : bidB);
  if (nonNilBid > 0) {
    if (tricks >= nonNilBid) {
      const projectedBags = tricks - nonNilBid;
      handPts += nonNilBid * 10 + projectedBags;
      if (gameState.teamBags[team] + projectedBags >= 10) handPts -= 100;
    } else handPts -= nonNilBid * 10;
  }
  const met = nonNilBid === 0 || tricks >= nonNilBid;
  const sign = handPts > 0 ? '+' : '';
  const cls = [
    'hand-points-chip',
    met ? 'hand-points-met' : '',
    idle ? 'hand-points-idle' : '',
  ].filter(Boolean).join(' ');
  return `
    <div class="${cls}">
      <span class="hand-points-label">Hand</span>
      <span class="hand-points-val">${sign}${handPts}</span>
      <span class="hand-points-sub">${tricks} / ${nonNilBid}</span>
    </div>
  `;
}

function renderPlayers(): void {
  renderAIPlayer((viewerSeat + 2) % 4, 'north-player');
  renderAIPlayer((viewerSeat + 1) % 4, 'west-player');
  renderAIPlayer((viewerSeat + 3) % 4, 'east-player');
}

function renderAIPlayer(playerIndex: number, containerId: string): void {
  const gameState = getState();
  if (!gameState) return;
  const container = el(containerId);
  const player = gameState.players[playerIndex];
  const isCurrent = gameState.currentPlayer === playerIndex && gameState.phase !== 'waiting';
  const color = PLAYER_COLORS[playerIndex];
  const sig = [player.cardCount, player.bid, player.tricks, player.isNilBid, isCurrent, gameState.phase, handPointsChipHTML(playerIndex)].join('|');
  if (lastPlayerSig[containerId] === sig && container.childElementCount) return;
  lastPlayerSig[containerId] = sig;
  container.innerHTML = '';
  container.className = `ai-player player-area-${playerIndex}${isCurrent ? ' active-player' : ''}`;
  const info = document.createElement('div');
  info.className = 'player-info';
  info.style.borderColor = color;
  info.innerHTML = `
    <div class="player-name" style="color:${color}">${player.name}${player.isHuman && player.joined ? ' 👤' : ''}</div>
    <div class="player-stats">
      ${player.bid !== null
        ? `<span class="bid-badge" style="background:${color}">Bid: ${player.isNilBid ? 'NIL' : player.bid}</span>`
        : (gameState.phase === 'bidding' && isCurrent ? '<span class="thinking">thinking…</span>' : '')}
      ${gameState.phase !== 'bidding' && gameState.phase !== 'waiting'
        ? `<span class="tricks-badge">Tricks: ${player.tricks}</span>` : ''}
    </div>
  `;
  container.appendChild(info);
  const cards = document.createElement('div');
  cards.className = 'ai-cards';
  const count = Math.min(player.cardCount, 13);
  for (let i = 0; i < count; i++) {
    cards.appendChild(makeCardBack());
  }
  container.appendChild(cards);
  const chip = document.createElement('div');
  chip.innerHTML = handPointsChipHTML(playerIndex);
  const node = chip.firstElementChild;
  if (node) container.appendChild(node);
}

function trickCardsToShow(): (Card | null)[] {
  const gameState = getState();
  if (!gameState) return [null, null, null, null];
  const live = gameState.currentTrick ?? [];
  if (live.some(c => c)) return live;
  if (showFinishedTrick) {
    const last = gameState.lastTrickCards;
    if (last && last.some(c => c)) return last;
  }
  return live;
}

function renderTrick(): void {
  const gameState = getState();
  if (!gameState) return;
  const trickArea = el('trick-area');
  const cards = trickCardsToShow();
  const sig = trickSignature(cards);
  if (sig === lastTrickSig && trickArea.childElementCount) return;
  lastTrickSig = sig;
  trickArea.innerHTML = '';
  const positions = [
    { p: (viewerSeat + 2) % 4, className: 'trick-north' },
    { p: (viewerSeat + 1) % 4, className: 'trick-west' },
    { p: (viewerSeat + 3) % 4, className: 'trick-east' },
    { p: viewerSeat, className: 'trick-south' },
  ];
  for (const { p, className } of positions) {
    const slot = makeTrickCardEl(cards[p] ?? null, p);
    slot.classList.add(className);
    trickArea.appendChild(slot);
  }
  if (gameState.spadesBreak) {
    const badge = document.createElement('div');
    badge.className = 'spades-broken-badge';
    badge.textContent = '♠ Spades Broken';
    trickArea.appendChild(badge);
  }
}

function renderHumanHand(): void {
  const gameState = getState();
  if (!gameState) return;
  const handEl = el('human-hand');
  const locked = playInFlight || showFinishedTrick;
  const cardsKey = gameState.humanHand.map(c => c.suit + c.rank).join(',');
  const playableKey = locked ? '' : gameState.validCardIndices.join(',');
  const sig = `${cardsKey}:${playableKey}`;
  if (sig !== lastHandSig || !handEl.childElementCount) {
    lastHandSig = sig;
    handEl.innerHTML = '';
    const hand = gameState.humanHand;
    const valid = new Set(gameState.validCardIndices);
    const total = hand.length;
    hand.forEach((card, i) => {
      const isPlayable = valid.has(i) && !locked;
      const cardEl = makeCardEl(card, isPlayable, i);
      const angle = total > 1 ? (i - (total - 1) / 2) * (Math.min(5, 60 / total)) : 0;
      const lift = Math.abs(angle) * 0.3;
      cardEl.style.transform = `rotate(${angle}deg) translateY(-${lift}px)`;
      cardEl.style.transformOrigin = 'bottom center';
      cardEl.style.marginLeft = i > 0 ? 'calc(var(--card-w) * -0.55)' : '0';
      cardEl.style.zIndex = String(i + 1);
      handEl.appendChild(cardEl);
    });
  } else {
    // Card list unchanged: only toggle playable classes.
    const valid = new Set(locked ? [] : gameState.validCardIndices);
    handEl.querySelectorAll<HTMLElement>('.card').forEach(c => {
      const idx = Number(c.dataset.cardIndex);
      c.classList.toggle('playable', valid.has(idx));
    });
  }

  const human = gameState.players[viewerSeat];
  const isCurrent = gameState.currentPlayer === viewerSeat && gameState.phase === 'playing';
  const seatLabel = viewerSeat !== 0 ? ` (${SEAT_NAMES[viewerSeat]})` : '';
  el('human-info').innerHTML = `
    <div class="player-name human-name${isCurrent ? ' active-name' : ''}">${playerName}${seatLabel}</div>
    <div class="player-stats">
      ${human.bid !== null
        ? `<span class="bid-badge" style="background:#3b82f6">Bid: ${human.isNilBid ? 'NIL' : human.bid}</span>`
        : ''}
      ${gameState.phase !== 'bidding' && gameState.phase !== 'waiting'
        ? `<span class="tricks-badge">Tricks: ${human.tricks}</span>` : ''}
    </div>
    ${handPointsChipHTML(viewerSeat)}
  `;
}

function renderActionPanel(): void {
  const gameState = getState();
  if (!gameState) return;
  const bidPanel = el('bid-panel');
  const msgPanel = el('action-message');
  bidPanel.classList.add('hidden');
  msgPanel.textContent = '';
  if (gameState.phase === 'waiting') {
    msgPanel.textContent = '⏳ Waiting for all players to join…';
  } else if (
    gameState.phase === 'bidding'
    && gameState.currentPlayer === viewerSeat
    && gameState.players[viewerSeat]?.bid == null
    && !bidInFlight
  ) {
    bidPanel.classList.remove('hidden');
  } else if (gameState.phase === 'bidding' && gameState.currentPlayer === viewerSeat) {
    msgPanel.textContent = 'Placing bid…';
  } else if (gameState.phase === 'bidding') {
    const cp = gameState.players[gameState.currentPlayer];
    msgPanel.textContent = `Waiting for ${cp.name} to bid…`;
  } else if (gameState.phase === 'playing' && (playInFlight || showFinishedTrick)) {
    msgPanel.textContent = '';
  } else if (gameState.phase === 'playing' && gameState.currentPlayer === viewerSeat) {
    msgPanel.textContent = '🃏 Your turn — click a card to play';
  } else if (gameState.phase === 'playing') {
    const cp = gameState.players[gameState.currentPlayer];
    msgPanel.textContent = `Waiting for ${cp.name}…`;
  } else if (gameState.phase === 'roundOver') {
    renderRoundOver();
  } else if (gameState.phase === 'gameOver') {
    renderGameOver();
  }
  // Another human may have dealt. The overlay only ever closed on our own
  // click, so it sat over the new hand until we pressed a button that then
  // failed with "Not in round-over phase".
  if (gameState.phase !== 'roundOver' && gameState.phase !== 'gameOver') {
    hideRoundOverlay();
  }
}

function renderRoundOver(): void {
  const gameState = getState();
  if (!gameState || !isGameScreenActive()) return;
  const overlay = el('round-overlay');
  const overlayKey = `round:${gameState.roundNumber}:${gameState.teamScores.join(',')}`;
  if (lastOverlayKey === overlayKey && !overlay.classList.contains('hidden')) return;
  lastOverlayKey = overlayKey;
  overlay.classList.remove('hidden');
  const gs = gameState;
  const viewerTeam = (viewerSeat === 0 || viewerSeat === 2) ? 0 : 1;
  const partnerLabel = SEAT_NAMES[(viewerSeat + 2) % 4];
  const opp1Label = SEAT_NAMES[(viewerSeat + 1) % 4];
  const opp2Label = SEAT_NAMES[(viewerSeat + 3) % 4];
  const usHandScore = gs.lastRoundScores ? gs.lastRoundScores[viewerTeam] : null;
  const themHandScore = gs.lastRoundScores ? gs.lastRoundScores[1 - viewerTeam] : null;

  const fmtHandScore = (pts: number | null): string => {
    if (pts === null) return '';
    return pts >= 0 ? `+${pts}` : `${pts}`;
  };
  const fmtSigned = (pts: number): string => (pts >= 0 ? `+${pts}` : `${pts}`);
  const renderScoreBreakdown = (team: number, label: string, color: string): string => {
    const breakdown = gs.lastRoundBreakdown?.[team] as RoundScoreBreakdown | undefined;
    if (!breakdown) return '';
    const playerRows = breakdown.players.map(player => `
      <div class="score-detail-player">
        <span>${player.name}${player.isNilBid ? ' (Nil)' : ''}</span>
        <span>Bid ${player.isNilBid ? 'Nil' : player.bid} · Tricks ${player.tricks}${player.nilPoints ? ` · Nil ${fmtSigned(player.nilPoints)}` : ''}</span>
      </div>
    `).join('');
    const bagPenalty = breakdown.bagPenalty
      ? `<div><span>Bag penalty</span><strong>${fmtSigned(breakdown.bagPenalty)}</strong></div>`
      : '';
    const contractLabel = breakdown.nonNilBid > 0
      ? `${breakdown.madeContract ? 'Made' : 'Missed'} ${breakdown.nonNilBid} bid`
      : 'No non-nil bid';
    return `
      <div class="score-detail-card">
        <div class="score-detail-title" style="color:${color}">${label}</div>
        <div class="score-detail-players">${playerRows}</div>
        <div class="score-detail-grid">
          <div><span>Cards/tricks counted</span><strong>${breakdown.teamTricks}</strong></div>
          <div><span>${contractLabel}</span><strong>${fmtSigned(breakdown.contractPoints)}</strong></div>
          <div><span>Bags this hand</span><strong>${breakdown.bagsTaken}</strong></div>
          <div><span>Bags total</span><strong>${breakdown.bagsBefore} → ${breakdown.bagsAfter}</strong></div>
          ${breakdown.nilPoints ? `<div><span>Nil total</span><strong>${fmtSigned(breakdown.nilPoints)}</strong></div>` : ''}
          ${bagPenalty}
        </div>
        <div class="score-detail-total">Hand total: <strong>${fmtSigned(breakdown.totalPoints)}</strong></div>
      </div>
    `;
  };

  overlay.innerHTML = `
    <div class="overlay-box round-over-box">
      <h2 class="overlay-title">🃏 Round ${gs.roundNumber} Over!</h2>
      <div class="round-scores">
        <div class="round-score-row">
          <span class="team-name-label" style="color:#10b981">🫂 You + ${partnerLabel}</span>
          <span class="round-score-val">${fmtHandScore(usHandScore)} <small style="opacity:0.55;font-size:0.7em">(${gs.teamScores[viewerTeam]} total)</small></span>
        </div>
        <div class="round-score-row">
          <span class="team-name-label" style="color:#ef4444">🤖 ${opp1Label} + ${opp2Label}</span>
          <span class="round-score-val">${fmtHandScore(themHandScore)} <small style="opacity:0.55;font-size:0.7em">(${gs.teamScores[1 - viewerTeam]} total)</small></span>
        </div>
      </div>
      <div class="score-details">
        ${renderScoreBreakdown(viewerTeam, `You + ${partnerLabel}`, '#10b981')}
        ${renderScoreBreakdown(1 - viewerTeam, `${opp1Label} + ${opp2Label}`, '#ef4444')}
      </div>
      <p class="overlay-hint">First to ${gs.winScore} wins!</p>
      <button id="next-round-btn" class="btn btn-primary btn-large">Deal Next Round ▶</button>
    </div>
  `;
  el('next-round-btn').addEventListener('click', () => { void handleNextRound(); });
}

function renderGameOver(): void {
  const gameState = getState();
  if (!gameState || !isGameScreenActive()) return;
  const overlay = el('round-overlay');
  const overlayKey = `over:${gameState.roundNumber}:${gameState.gameResult}`;
  if (lastOverlayKey === overlayKey && !overlay.classList.contains('hidden')) return;
  lastOverlayKey = overlayKey;
  overlay.classList.remove('hidden');
  const isTeam0 = viewerSeat === 0 || viewerSeat === 2;
  const team0Won = gameState.gameResult === 'win';
  const iWon = isTeam0 ? team0Won : !team0Won;
  overlay.innerHTML = `
    <div class="overlay-box game-over-box ${iWon ? 'win' : 'loss'}">
      <div class="game-over-emoji">${iWon ? '🏆' : '💔'}</div>
      <h2 class="overlay-title">${iWon ? 'YOU WIN!' : 'GAME OVER'}</h2>
      <div class="final-scores">
        <div>🫂 You + North: <strong>${gameState.teamScores[0]}</strong></div>
        <div>🤖 East + West: <strong>${gameState.teamScores[1]}</strong></div>
        <div class="rounds-played">Played ${gameState.roundNumber} rounds</div>
      </div>
      <div class="game-over-buttons">
        <button id="play-again-btn" class="btn btn-primary btn-large">Play Again ♠</button>
        <button id="back-menu-btn" class="btn btn-secondary">Main Menu</button>
      </div>
    </div>
  `;
  el('play-again-btn').addEventListener('click', () => {
    leaveGame();
    showScreen('setup-screen');
  });
  el('back-menu-btn').addEventListener('click', () => {
    leaveGame();
    showScreen('menu-screen');
  });
}

function renderMessage(): void {
  const gameState = getState();
  if (!gameState) return;
  el('game-message').textContent = gameState.message;
}

export function hideRoundOverlay(): void {
  el('round-overlay').classList.add('hidden');
  lastOverlayKey = '';
}

export function setupHandClicks(): void {
  el('human-hand').addEventListener('click', ev => {
    const card = (ev.target as HTMLElement).closest<HTMLElement>('#human-hand .card.playable');
    if (!card) return;
    const idx = Number(card.dataset.cardIndex);
    if (Number.isFinite(idx)) void handlePlayCard(idx);
  });
}

export function setupBidPanel(): void {
  const panel = el('bid-panel');
  panel.innerHTML = `
    <div class="bid-panel-inner">
      <div class="bid-label">Your Bid (0 = Nil)</div>
      <div class="bid-buttons" id="bid-buttons"></div>
    </div>
  `;
  const buttons = el('bid-buttons');
  for (let i = 0; i <= 13; i++) {
    const btn = document.createElement('button');
    btn.className = 'bid-btn';
    btn.textContent = i === 0 ? 'NIL' : String(i);
    btn.addEventListener('click', () => { void handleBid(i); });
    buttons.appendChild(btn);
  }
}

export function renderHistory(data: HistoryResponse): void {
  const records = buildTeamRecords(data.games);
  const content = el('history-content');
  const total = data.totalWins + data.totalLosses;
  const winPct = total > 0 ? Math.round((data.totalWins / total) * 100) : 0;
  content.innerHTML = `
    <div class="history-summary">
      <div class="stat-box wins"><span class="stat-num">${data.totalWins}</span><span class="stat-label">Wins</span></div>
      <div class="stat-box losses"><span class="stat-num">${data.totalLosses}</span><span class="stat-label">Losses</span></div>
      <div class="stat-box rate"><span class="stat-num">${winPct}%</span><span class="stat-label">Win Rate</span></div>
    </div>
    ${data.games.length === 0
      ? '<p class="no-stats">No games played yet!</p>'
      : `<table class="stats-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Result</th>
              <th class="num">Score</th>
              <th class="num">Rounds</th>
              <th>Your Team</th>
              <th>Opponents</th>
            </tr>
          </thead>
          <tbody>
            ${data.games.map(g => renderHistoryRow(g, records)).join('')}
          </tbody>
        </table>`}
  `;
}

type TeamRecords = Map<string, { wins: number; losses: number }>;

function buildTeamRecords(games: HistoryGame[]): TeamRecords {
  const records: TeamRecords = new Map();
  for (const g of games) {
    const n = g.playerNames;
    const usKey = [n[0], n[2]].sort().join('+');
    const themKey = [n[1], n[3]].sort().join('+');
    const won = g.result === 'win';
    const us = records.get(usKey) ?? { wins: 0, losses: 0 };
    won ? us.wins++ : us.losses++;
    records.set(usKey, us);
    const them = records.get(themKey) ?? { wins: 0, losses: 0 };
    won ? them.losses++ : them.wins++;
    records.set(themKey, them);
  }
  return records;
}

function renderHistoryRow(game: HistoryGame, records: TeamRecords): string {
  const won = game.result === 'win';
  const date = new Date(game.playedAt).toLocaleDateString();
  const n = game.playerNames;
  const t = game.playerTricks;
  const team0 = `${n[0]}&nbsp;(${t[0]}) + ${n[2]}&nbsp;(${t[2]})`;
  const team1 = `${n[1]}&nbsp;(${t[1]}) + ${n[3]}&nbsp;(${t[3]})`;
  const usKey = [n[0], n[2]].sort().join('+');
  const themKey = [n[1], n[3]].sort().join('+');
  const usRec = records.get(usKey);
  const themRec = records.get(themKey);
  const usTag = usRec ? ` <span class="team-record">${usRec.wins}W-${usRec.losses}L</span>` : '';
  const themTag = themRec ? ` <span class="team-record">${themRec.wins}W-${themRec.losses}L</span>` : '';
  return `
    <tr class="${won ? 'stat-win' : 'stat-loss'}">
      <td class="stat-date">${date}</td>
      <td class="stat-result">${won ? '🏆 Win' : '💔 Loss'}</td>
      <td class="num">${game.playerTeamScore} – ${game.opponentTeamScore}</td>
      <td class="num">${game.roundsPlayed}</td>
      <td class="stat-team-us">${team0}${usTag}</td>
      <td class="stat-team-them">${team1}${themTag}</td>
    </tr>
  `;
}
